import { FileLock2, KeyRound, EyeOff, ShieldCheck, ReceiptText, UserX, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FadeUp, PageHero, Eyebrow } from "@/components/Reveal";

const PHASES = [
  { n: "01", title: "Onboarding & Blindatura Privacy", text: "Sottoscrizione degli accordi di riservatezza (NDA), messa in sicurezza degli accessi e definizione rigorosa dei parametri di anonimato prima di qualsiasi operatività." },
  { n: "02", title: "Discovery & Posizionamento Iconico", text: "Analisi del profilo, definizione del target di riferimento e progettazione di un'identità visiva esclusiva che distingua il tuo brand dalla concorrenza." },
  { n: "03", title: "Regia Contenuti & Crescita Social", text: "Pianificazione editoriale per Instagram e TikTok, concept visivi per gli shooting e strategie di traffico organico per attrarre costantemente nuovo pubblico in target." },
  { n: "04", title: "Architettura di Vendita & Chat 24/7", text: "Integrazione del team dedicato di vendita: presidio costante dei messaggi, script personalizzati sul tuo tono di voce e gestione dei funnel ad alta conversione." },
  { n: "05", title: "Analisi Dati & Scalabilità Continua", text: "Monitoraggio continuo dei KPI, ottimizzazione del pricing e sviluppo di nuove linee di ricavo per scalare i risultati mese dopo mese." },
];

const PROTOCOL = [
  { icon: <FileLock2 size={20} />, title: "NDA su ogni collaborazione", text: "Ogni rapporto con IEM è coperto da accordo di riservatezza, per creator e team.", testid: "privacy-nda" },
  { icon: <KeyRound size={20} />, title: "Accessi riservati", text: "Credenziali gestite con sistemi sicuri e accessi limitati al solo personale autorizzato.", testid: "privacy-access" },
  { icon: <EyeOff size={20} />, title: "Identità protetta", text: "Anonimato e separazione tra identità personale e presenza digitale, quando richiesto.", testid: "privacy-identity" },
  { icon: <ShieldCheck size={20} />, title: "Protezione contenuti", text: "Monitoraggio e rimozione di diffusioni non autorizzate dei contenuti.", testid: "privacy-content" },
  { icon: <ReceiptText size={20} />, title: "Trasparenza economica", text: "Report chiari e pagamenti tracciati. Sai sempre cosa guadagni e perché.", testid: "privacy-payments" },
  { icon: <UserX size={20} />, title: "Nessuna condivisione", text: "Nessun dato condiviso con terzi senza il tuo consenso esplicito. Mai.", testid: "privacy-sharing" },
];

export default function MetodoPrivacy() {
  return (
    <main data-testid="metodo-page">
      <PageHero
        testid="metodo-hero"
        eyebrow="Metodo & Privacy"
        lines={[
          "Processi rigorosi.",
          <span key="l2" className="gold-text gold-text-animated italic">Riservatezza assoluta.</span>,
        ]}
        sub="Il metodo IEM applica gli standard di gestione aziendale alla creator economy: blindatura legale, controllo operativo totale e scalabilità strategica."
      />

      <section className="py-14 lg:py-20" data-testid="method-phases">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Eyebrow>Il metodo IEM</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F5F3EE]">
              Cinque fasi. <span className="italic text-gold">Metodo rigoroso, zero improvvisazione.</span>
            </h2>
          </FadeUp>
          <div className="mt-12">
            {PHASES.map((p, i) => (
              <FadeUp key={p.n} delay={i * 0.05}>
                <div
                  data-testid={`phase-${p.n}`}
                  className="grid grid-cols-[auto_1fr] items-baseline gap-6 border-t border-[#1A1A1A] py-9 last:border-b sm:grid-cols-[100px_1fr_1.2fr] lg:gap-12"
                >
                  <span className="font-serif text-2xl sm:text-3xl text-gold/80">{p.n}</span>
                  <h3 className="font-serif text-xl sm:text-2xl text-[#F5F3EE]">{p.title}</h3>
                  <p className="col-span-2 sm:col-span-1 text-sm leading-relaxed text-[#969696]">{p.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#1A1A1A] py-14 lg:py-20" data-testid="privacy-protocol">
        <div className="mx-auto max-w-7xl px-6">
          <FadeUp>
            <Eyebrow>Protocollo privacy</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-5 max-w-2xl font-serif text-2xl sm:text-3xl lg:text-4xl text-[#F5F3EE]">
              La discrezione non è un'opzione. <span className="italic text-gold">È lo standard.</span>
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-px border border-[#1A1A1A] bg-[#1A1A1A] sm:grid-cols-2 lg:grid-cols-3">
            {PROTOCOL.map((p, i) => (
              <FadeUp key={p.title} delay={i * 0.06} className="group bg-[#0b0b0b] p-8 transition-colors duration-500 hover:bg-[#111111]">
                <div data-testid={p.testid}>
                  <div className="flex h-11 w-11 items-center justify-center border border-gold/25 text-gold transition-colors duration-500 group-hover:border-gold/60">
                    {p.icon}
                  </div>
                  <h3 className="mt-6 font-serif text-lg sm:text-xl text-[#F5F3EE]">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#969696]">{p.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2} className="mt-12">
            <Link
              data-testid="metodo-faq-cta"
              to="/faq"
              className="inline-flex items-center gap-3 border border-gold/50 px-7 py-4 text-[12px] font-semibold tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-[#080808]"
            >
              DOMANDE FREQUENTI <ArrowRight size={14} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
